import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  Building2,
  FileText,
  FolderKanban,
  Globe2,
  LogOut,
  Palette,
  Send,
  ShieldCheck,
  Sparkles,
  Target,
  Trash2,
  UserRound,
  Workflow,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { getSession, inviteBusinessCenterUser, login, logout } from "../../lib/api";
import { defaultLandingWorkspaceOptions, getDefaultLandingWorkspaceLabel, normalizeDefaultLandingWorkspace } from "../../lib/defaultLandingWorkspace";
import type { BusinessCenterCompanyRecord, BusinessCenterUserRecord } from "../../lib/types";
import {
  buildCompanyUserRoleSummary,
  companyStatusOptions,
  companyUserRoleOptions,
  mapCompanyRoleToPermissionRole,
  portalAccessOptions,
  removeBusinessCenterCompanyUser,
  refreshBusinessCenterCompanies,
  refreshBusinessCenterCompanyUsers,
  updateBusinessCenterCompanyUser,
} from "./businessCenterCompanyUsersData";

const businessCenterNavLinks = [
  { label: "Business Center OS Home", to: "/admin/business-center-os", icon: BarChart3 },
  { label: "CRM / Leads", to: "/admin/leads", icon: Target },
  { label: "Client Center", to: "/admin/truevance-client-center", icon: UserRound },
  { label: "Project Center", to: "/admin/truevance-project-center", icon: Workflow },
  { label: "Tasks Center", to: "/admin/truevance-tasks", icon: FolderKanban },
  { label: "Document Center", to: "/admin/truevance-document-center", icon: FileText },
  { label: "Universal User Management", to: "/admin/universal-user-management", icon: Users },
  { label: "Client Onboarding Center", to: "/admin/client-onboarding-center", icon: Users },
  { label: "Workspace Provisioning Center", to: "/admin/workspace-provisioning-center", icon: Globe2 },
  { label: "White Label Branding Center", to: "/admin/white-label-branding-center", icon: Palette },
  { label: "Automation Engine", to: "/admin/automation-engine", icon: Workflow },
  { label: "Roles & Permissions", to: "/admin/truevance-roles-center", icon: ShieldCheck },
  { label: "Reporting", to: "/admin/truevance-analytics-center", icon: BarChart3 },
  { label: "Billing", to: "/admin/payment-records", icon: FileText },
  { label: "CollabRecover AR Solutions", to: "/admin/ar-recovery", icon: BarChart3 },
  { label: "Settings", to: "/admin/truevance-settings-center", icon: Sparkles },
] as const;

interface CompanyUserFormState {
  companyId: string;
  firstName: string;
  lastName: string;
  displayName: string;
  fullName: string;
  email: string;
  jobTitle: string;
  department: string;
  phone: string;
  team: string;
  notificationPreference: NonNullable<BusinessCenterUserRecord["notificationPreference"]>;
  role: BusinessCenterUserRecord["role"];
  status: BusinessCenterUserRecord["status"];
  portalAccessStatus: BusinessCenterUserRecord["portalAccessStatus"];
  defaultLandingWorkspace: string;
  assignedClientIdsText: string;
  assignedProjectIdsText: string;
  assignedTaskIdsText: string;
  assignedDocumentIdsText: string;
  notes: string;
}

const blankForm: CompanyUserFormState = {
  companyId: "",
  firstName: "",
  lastName: "",
  displayName: "",
  fullName: "",
  email: "",
  jobTitle: "",
  department: "Recovery Operations",
  phone: "",
  team: "AR Recovery",
  notificationPreference: "both",
  role: "company_team_member",
  status: "active",
  portalAccessStatus: "active",
  defaultLandingWorkspace: "recovery-workbench",
  assignedClientIdsText: "",
  assignedProjectIdsText: "",
  assignedTaskIdsText: "",
  assignedDocumentIdsText: "",
  notes: "",
};

function buildNewUserForm(companyId: string): CompanyUserFormState {
  return {
    ...blankForm,
    companyId,
    assignedClientIdsText: companyId,
  };
}

export function BusinessCenterOSCompanyUsersPage() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<BusinessCenterCompanyRecord[]>([]);
  const [users, setUsers] = useState<BusinessCenterUserRecord[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState<CompanyUserFormState>(blankForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingLoginCode, setSendingLoginCode] = useState(false);
  const [message, setMessage] = useState("");
  const [sessionUserEmail, setSessionUserEmail] = useState("");
  const [pendingDeactivateUserId, setPendingDeactivateUserId] = useState("");

  useBusinessCenterOSBranding(
    "Universal User Management Center",
    "Shared company-scoped user management for Business Center OS with reusable role mapping and tenant access control foundations.",
  );

  useEffect(() => {
    let cancelled = false;
    Promise.all([refreshBusinessCenterCompanies(), refreshBusinessCenterCompanyUsers()])
      .then(([nextCompanies, nextUsers]) => {
        if (cancelled) return;
        setCompanies(nextCompanies);
        setUsers(nextUsers);
        const defaultCompanyId = nextCompanies.find((company) => company.companyType === "parent")?.id || nextCompanies[0]?.id || "";
        setSelectedCompanyId(defaultCompanyId);
        setForm((current) => ({ ...current, companyId: defaultCompanyId }));
        setSelectedUserId(nextUsers.find((user) => user.companyId === defaultCompanyId)?.id || nextUsers[0]?.id || "");
      })
      .catch((error) => {
        if (cancelled) return;
        setCompanies([]);
        setUsers([]);
        setSelectedCompanyId("");
        setSelectedUserId("");
        setMessage(error instanceof Error
          ? `Unable to load persisted user management data. ${error.message}`
          : "Unable to load persisted user management data.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getSession()
      .then((session) => {
        if (!cancelled) setSessionUserEmail(session.user?.email || "");
      })
      .catch(() => {
        if (!cancelled) setSessionUserEmail("");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setPendingDeactivateUserId("");
    }

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  const visibleUsers = useMemo(() => {
    const normalizedQuery = search.trim().toLowerCase();
    return users.filter((user) => {
      const matchesCompany = !selectedCompanyId || user.companyId === selectedCompanyId;
      const matchesQuery =
        !normalizedQuery ||
        `${user.fullName} ${user.email} ${user.role} ${user.status} ${user.portalAccessStatus}`.toLowerCase().includes(normalizedQuery);
      return matchesCompany && matchesQuery;
    });
  }, [search, selectedCompanyId, users]);

  const selectedCompany = companies.find((company) => company.id === selectedCompanyId) || companies[0] || null;
  const selectedUser = !isCreatingUser && selectedUserId ? users.find((user) => user.id === selectedUserId) || null : null;
  const selectedUserCanReceiveAccessCode = Boolean(
    selectedUser?.email.trim()
    && selectedUser.status === "active"
    && selectedUser.portalAccessStatus === "active"
    && isInternalCompanyUserRole(selectedUser.role),
  );
  const selectedUserCanDeactivate = Boolean(
    selectedUser
    && selectedUser.status === "active"
    && normalizeEmail(selectedUser.email) !== normalizeEmail(sessionUserEmail),
  );

  const summary = useMemo(() => {
    const roleSummary = buildCompanyUserRoleSummary(users);
    return [
      { label: "Companies", value: companies.length, note: "Parent and client companies in the relationship model." },
      { label: "Parent account", value: companies.filter((company) => company.companyType === "parent").length, note: "Root scope for future multi-company ownership." },
      { label: "Active users", value: users.filter((user) => user.status === "active").length, note: "Company-scoped users currently active." },
      { label: "Portal active", value: users.filter((user) => user.portalAccessStatus === "active").length, note: "Users ready for portal access." },
      ...roleSummary.map((item) => ({
        label: item.label,
        value: item.value,
        note: `${item.permissionRole.replace(/_/g, " ")} mapping`,
      })),
    ];
  }, [companies, users]);

  function updateForm<K extends keyof CompanyUserFormState>(key: K, value: CompanyUserFormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function loadUserIntoForm(user: BusinessCenterUserRecord) {
    setIsCreatingUser(false);
    setSelectedUserId(user.id);
    setSelectedCompanyId(user.companyId);
    setForm({
      companyId: user.companyId,
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      displayName: user.displayName || user.fullName,
      fullName: user.fullName,
      email: user.email,
      jobTitle: user.jobTitle || "",
      department: user.department || "",
      phone: user.phone || "",
      team: user.team || "",
      notificationPreference: user.notificationPreference || "both",
      role: user.role,
      status: user.status,
      portalAccessStatus: user.portalAccessStatus,
      defaultLandingWorkspace: normalizeDefaultLandingWorkspace(user.defaultLandingWorkspace),
      assignedClientIdsText: user.assignedClientIds.join("\n"),
      assignedProjectIdsText: user.assignedProjectIds.join("\n"),
      assignedTaskIdsText: user.assignedTaskIds.join("\n"),
      assignedDocumentIdsText: user.assignedDocumentIds.join("\n"),
      notes: "",
    });
  }

  useEffect(() => {
    if (selectedUser) loadUserIntoForm(selectedUser);
  }, [selectedUser, selectedUserId]);

  function handleNewUser() {
    const selectedCompanyForInvite = companies.find((company) => company.id === selectedCompanyId);
    const arCompanyId = companies.find((company) => company.companySlug === "truevance-connect" || company.id === "company-truevance-connect")?.id || "";
    const companyId = selectedCompanyForInvite && selectedCompanyForInvite.companyType !== "parent"
      ? selectedCompanyForInvite.id
      : arCompanyId || selectedCompanyId || companies[0]?.id || "";
    setIsCreatingUser(true);
    setSelectedUserId("");
    setSelectedCompanyId(companyId);
    setSearch("");
    setMessage("New user mode is ready. Enter the team member details and choose Invite user.");
    setForm(buildNewUserForm(companyId));
  }

  async function handleInvite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form.companyId) return;
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const fullName = form.fullName.trim() || `${firstName} ${lastName}`.trim();
    const email = form.email.trim();
    if (!fullName || !email) {
      setMessage("Full name and email are required before inviting an internal user.");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      const company = companies.find((item) => item.id === form.companyId);
      const nextUser: BusinessCenterUserRecord = {
        id: crypto.randomUUID(),
        companyId: form.companyId,
        parentCompanyId: company?.parentCompanyId || null,
        firstName,
        lastName,
        displayName: form.displayName.trim() || fullName,
        fullName,
        email,
        jobTitle: form.jobTitle.trim(),
        department: form.department.trim(),
        phone: form.phone.trim(),
        team: form.team.trim(),
        notificationPreference: form.notificationPreference,
        role: form.role,
        status: "invited",
        portalAccessStatus: "pending",
        defaultLandingWorkspace: normalizeDefaultLandingWorkspace(form.defaultLandingWorkspace),
        invitationStatus: "pending",
        invitationMessage: form.notes.trim() || "Welcome to the CollabRecover AR Solutions recovery operations portal.",
        assignedClientIds: splitList(form.assignedClientIdsText),
        assignedProjectIds: splitList(form.assignedProjectIdsText),
        assignedTaskIds: splitList(form.assignedTaskIdsText),
        assignedDocumentIds: splitList(form.assignedDocumentIdsText),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      const invitation = await inviteBusinessCenterUser({
        user: nextUser,
        companyName: company?.companyName || "CollabRecover AR Solutions",
        invitationMessage: nextUser.invitationMessage,
      });
      const nextUsers = invitation.users;
      const savedUser = nextUsers.find((user) =>
        user.companyId === nextUser.companyId && normalizeEmail(user.email) === normalizeEmail(nextUser.email),
      ) || invitation.user || nextUser;
      setUsers(nextUsers);
      setMessage(
        `Invitation email sent to ${savedUser.fullName}. Invitation Status: Pending.${invitation.deliveryMessageId ? ` Provider reference: ${invitation.deliveryMessageId}` : ""}`,
      );
      setIsCreatingUser(false);
      setSelectedUserId(savedUser.id);
      setForm(buildNewUserForm(form.companyId));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to invite the user.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveUser(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedUser) return;
    setSaving(true);
    setMessage("");
    try {
      const company = companies.find((item) => item.id === form.companyId);
      const nextUsers = await updateBusinessCenterCompanyUser(selectedUser.id, {
        companyId: form.companyId || selectedUser.companyId,
        parentCompanyId: company?.parentCompanyId || selectedUser.parentCompanyId || null,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        displayName: form.displayName.trim() || form.fullName.trim() || selectedUser.displayName || selectedUser.fullName,
        fullName: form.fullName.trim() || selectedUser.fullName,
        email: form.email.trim() || selectedUser.email,
        jobTitle: form.jobTitle.trim(),
        department: form.department.trim(),
        phone: form.phone.trim(),
        team: form.team.trim(),
        notificationPreference: form.notificationPreference,
        role: form.role,
        status: form.status,
        portalAccessStatus: form.portalAccessStatus,
        defaultLandingWorkspace: normalizeDefaultLandingWorkspace(form.defaultLandingWorkspace),
        assignedClientIds: splitList(form.assignedClientIdsText),
        assignedProjectIds: splitList(form.assignedProjectIdsText),
        assignedTaskIds: splitList(form.assignedTaskIdsText),
        assignedDocumentIds: splitList(form.assignedDocumentIdsText),
      });
      setUsers(nextUsers);
      setMessage(`${form.fullName || selectedUser.fullName} was updated with company-scoped access.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to update the user.");
    } finally {
      setSaving(false);
    }
  }

  function handleOpenDeactivateConfirmation() {
    if (!selectedUser) {
      setMessage("Select a user before deactivating access.");
      return;
    }
    if (normalizeEmail(selectedUser.email) === normalizeEmail(sessionUserEmail)) {
      setMessage("You cannot deactivate your own administrator account from this workflow.");
      return;
    }
    if (selectedUser.status !== "active") {
      setMessage(`${selectedUser.fullName} is not active, so deactivation is unavailable.`);
      return;
    }
    setPendingDeactivateUserId(selectedUser.id);
  }

  async function handleDeactivateUser(userId: string) {
    const targetUser = users.find((user) => user.id === userId);
    if (!targetUser) return;
    if (normalizeEmail(targetUser.email) === normalizeEmail(sessionUserEmail)) {
      setPendingDeactivateUserId("");
      setMessage("You cannot deactivate your own administrator account from this workflow.");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      const nextUsers = await updateBusinessCenterCompanyUser(userId, {
        status: "deactivated",
        portalAccessStatus: "suspended",
      });
      setUsers(nextUsers);
      setPendingDeactivateUserId("");
      setMessage(`${targetUser.fullName} was deactivated for the selected company.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to pause user access.");
    } finally {
      setSaving(false);
    }
  }

  function handleAssignRole() {
    if (!selectedUser) {
      setMessage("Select a user before assigning a role.");
      return;
    }
    loadUserIntoForm(selectedUser);
    setMessage(`Role assignment is ready for ${selectedUser.fullName}. Choose a supported role and save the user.`);
  }

  async function handleResendLoginCode() {
    if (
      !selectedUser
      || !selectedUser.email.trim()
      || selectedUser.status !== "active"
      || selectedUser.portalAccessStatus !== "active"
      || !isInternalCompanyUserRole(selectedUser.role)
    ) {
      setMessage("Unable to send login code. Please check user status, portal access, role, and email.");
      return;
    }

    setSendingLoginCode(true);
    setMessage("");
    try {
      const delivery = await login(selectedUser.email, true);
      if (delivery.deliveryStatus !== "accepted_by_brevo" && delivery.deliveryStatus !== "accepted") {
        throw new Error("The login-code request was not accepted by the email provider.");
      }
      setMessage(
        `Login code sent to ${selectedUser.email}.${delivery.deliveryMessageId ? ` Provider reference: ${delivery.deliveryMessageId}` : ""}`,
      );
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to send login code. Please check user status and email.");
    } finally {
      setSendingLoginCode(false);
    }
  }

  async function handleDeleteUser(userId: string) {
    setSaving(true);
    setMessage("");
    try {
      const nextUsers = await removeBusinessCenterCompanyUser(userId);
      setUsers(nextUsers);
      const nextSelectedUserId = nextUsers.find((user) => user.companyId === selectedCompanyId)?.id || nextUsers[0]?.id || "";
      setSelectedUserId(nextSelectedUserId);
      if (!nextSelectedUserId) {
        setForm((current) => ({ ...blankForm, companyId: current.companyId || selectedCompanyId }));
      }
      setMessage("User record was removed from the company workspace.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to remove the user record.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <ShellLoading />;
  }

  return (
    <main className="business-center-os-shell min-h-screen text-[#F8FAFC]">
      <BusinessCenterOSHeader />

      <section className="business-center-os-hero border-b border-[rgba(6,182,212,.18)] px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-[1.15fr_.85fr] lg:items-end">
          <div className="grid gap-4">
            <div className="business-center-os-pill inline-flex w-fit items-center gap-2 rounded-full px-3 py-2 text-xs font-black uppercase tracking-[0.22em]">
              <Users className="size-4" aria-hidden="true" />
              Business Center OS
            </div>
            <h1 className="max-w-4xl font-display text-5xl font-bold leading-tight sm:text-6xl">Universal User Management Center</h1>
            <p className="max-w-3xl text-base font-semibold leading-8 text-[#E2E8F0]">
              Define parent-company accounts, company admins, company managers, team members, client users, and
              viewers without changing authentication or production enforcement.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/admin/business-center-os" className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold">
                <ArrowLeft className="size-4" aria-hidden="true" />
                Back to Business Center OS
              </Link>
              <Link to="/admin/truevance-roles-center" className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold">
                <ArrowRight className="size-4 rotate-180" aria-hidden="true" />
                Roles & Permissions
              </Link>
            </div>
          </div>

          <article className="business-center-os-card business-center-os-card--dark rounded-3xl p-5 sm:p-6">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-[#06B6D4]">Workspace focus</p>
            <h2 className="mt-2 font-display text-3xl font-bold text-[#F8FAFC]">Scoped access and parent-company control</h2>
            <p className="mt-3 text-sm font-semibold leading-7 text-[#E2E8F0]">
              This pass creates the management shell for company-scoped users so future permission enforcement can
              reference a clear parent-child relationship model.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {summary.slice(0, 4).map((metric) => (
                <div key={metric.label} className="rounded-3xl border border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.04)] p-4">
                  <p className="text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">{metric.label}</p>
                  <p className="mt-2 text-3xl font-bold text-[#F8FAFC]">{metric.value}</p>
                  <p className="mt-2 text-sm font-semibold leading-6 text-[#CBD5E1]">{metric.note}</p>
                </div>
              ))}
            </div>
          </article>
        </div>
      </section>

      <section className="px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {summary.map((metric) => (
              <article key={metric.label} className="business-center-os-card business-center-os-card--light rounded-3xl p-5">
                <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">{metric.label}</p>
                <p className="mt-2 text-4xl font-bold text-[#1F2937]">{metric.value}</p>
                <p className="mt-3 text-sm font-semibold leading-7 text-[#475569]">{metric.note}</p>
              </article>
            ))}
          </div>

          <section className="mt-8 grid gap-6 xl:grid-cols-[.95fr_1.05fr]">
            <article className="business-center-os-card business-center-os-card--dark rounded-3xl p-5 sm:p-6">
              <div className="flex flex-col gap-4 border-b border-[rgba(6,182,212,.16)] pb-4">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Company tree</p>
                  <h2 className="mt-1 font-display text-3xl font-bold text-[#F8FAFC]">Parent-child relationships</h2>
                </div>
                <label className="grid gap-2 text-sm font-extrabold text-[#F8FAFC]">
                  Select company
                  <select
                    value={selectedCompanyId}
                    onChange={(event) => {
                      setSelectedCompanyId(event.target.value);
                      setForm((current) => ({ ...current, companyId: event.target.value }));
                      if (!isCreatingUser) {
                        setSelectedUserId(users.find((user) => user.companyId === event.target.value)?.id || "");
                      }
                    }}
                    className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold"
                  >
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.companyName}
                      </option>
                    ))}
                  </select>
              </label>

              {selectedCompany ? (
                <div className="rounded-3xl border border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.04)] p-4">
                  <div className="flex items-start gap-3">
                    <span className="grid size-10 shrink-0 place-items-center rounded-full bg-[rgba(34,224,208,.14)] text-[#22E0D0]">
                      <Building2 className="size-4" aria-hidden="true" />
                    </span>
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">Active scope</p>
                      <h3 className="mt-1 font-display text-2xl font-bold text-[#F8FAFC]">{selectedCompany.companyName}</h3>
                      <p className="mt-2 text-sm font-semibold leading-7 text-[#E2E8F0]">
                        {selectedCompany.companyType.replace(/_/g, " ")}
                        <br />
                        Parent company: {selectedCompany.parentCompanyId ? companies.find((company) => company.id === selectedCompany.parentCompanyId)?.companyName || selectedCompany.parentCompanyId : "None"}
                        <br />
                        Brand: {selectedCompany.brand}
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

              <div className="mt-5 grid gap-3">
                {companies.map((company) => {
                  const userCount = users.filter((user) => user.companyId === company.id).length;
                  const isSelected = company.id === selectedCompanyId;
                  return (
                    <button
                      key={company.id}
                      type="button"
                      onClick={() => {
                        setSelectedCompanyId(company.id);
                        setForm((current) => ({ ...current, companyId: company.id }));
                        if (!isCreatingUser) {
                          setSelectedUserId(users.find((user) => user.companyId === company.id)?.id || "");
                        }
                      }}
                      className={`rounded-3xl border p-4 text-left transition ${
                        isSelected
                          ? "border-[rgba(34,224,208,.4)] bg-[rgba(34,224,208,.1)]"
                          : "border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.04)] hover:border-[rgba(34,224,208,.26)]"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-xs font-black uppercase tracking-[0.16em] text-[#22E0D0]">{company.companyType.replace(/_/g, " ")}</p>
                          <h3 className="mt-1 font-display text-2xl font-bold text-[#F8FAFC]">{company.companyName}</h3>
                        </div>
                        <span className="rounded-full border border-[rgba(34,224,208,.18)] px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-[#22E0D0]">
                          {userCount} users
                        </span>
                      </div>
                      <p className="mt-3 text-sm font-semibold leading-7 text-[#E2E8F0]">
                        Parent: {company.parentCompanyId ? companies.find((item) => item.id === company.parentCompanyId)?.companyName || company.parentCompanyId : "None"}
                        <br />
                        Brand: {company.brand}
                        <br />
                        Status: {company.status}
                      </p>
                    </button>
                  );
                })}
              </div>
            </article>

            <article className="business-center-os-card business-center-os-card--light rounded-3xl p-5 sm:p-6">
              <div className="flex flex-col gap-4 border-b border-[rgba(31,41,55,.12)] pb-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-[#14B8A6]">User management</p>
                    <h2 className="mt-1 font-display text-3xl font-bold text-[#1F2937]">Invite and update users</h2>
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                    <button
                      type="button"
                      onClick={handleNewUser}
                      className="business-center-os-button business-center-os-button--primary focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-full px-4 text-sm font-extrabold"
                    >
                      Invite User
                    </button>
                    <label className="grid gap-2 text-sm font-extrabold text-[#1F2937]">
                      Search
                      <input
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        className="business-center-os-field focus-ring min-h-12 rounded-2xl px-4 text-sm font-semibold"
                        placeholder="Search users, roles, or status"
                      />
                    </label>
                  </div>
                </div>

                <form onSubmit={selectedUser ? handleSaveUser : handleInvite} className="grid gap-4">
                  <div className="rounded-3xl border border-[rgba(20,184,166,.14)] bg-white p-4" aria-live="polite">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <div>
                        <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">
                          {selectedUser ? "Selected user" : "Invitation form"}
                        </p>
                        <h3 className="mt-1 font-display text-2xl font-bold text-[#1F2937]">
                          {selectedUser ? selectedUser.fullName : "Create a company user"}
                        </h3>
                        <p className="mt-2 text-sm font-semibold leading-7 text-[#475569]">
                          {selectedUser
                            ? `${selectedUser.email} - ${labelize(selectedUser.status)} - ${labelize(selectedUser.portalAccessStatus)}`
                            : "Enter required identity details, role, team, and company scope before inviting."}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={handleNewUser}
                          disabled={saving}
                          className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold disabled:opacity-60"
                        >
                          Invite User
                        </button>
                        <button
                          type="button"
                          onClick={handleOpenDeactivateConfirmation}
                          disabled={!selectedUserCanDeactivate || saving}
                          title={!selectedUser ? "Select a user first." : !selectedUserCanDeactivate ? "Only another active user can be deactivated." : undefined}
                          className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          Deactivate User
                        </button>
                        <button
                          type="button"
                          disabled={!selectedUserCanReceiveAccessCode || sendingLoginCode}
                          title={!selectedUser ? "Select a user first." : !selectedUserCanReceiveAccessCode ? "User must be active, portal-active, internal, and have an email." : undefined}
                          onClick={handleResendLoginCode}
                          className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <Send className="size-4" aria-hidden="true" />
                          {sendingLoginCode ? "Sending..." : "Send Access Code"}
                        </button>
                        <button
                          type="button"
                          onClick={handleAssignRole}
                          disabled={!selectedUser || saving}
                          title={!selectedUser ? "Select a user first." : undefined}
                          className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          Assign Role
                        </button>
                      </div>
                    </div>
                    {!selectedUser ? (
                      <p className="mt-3 text-sm font-semibold leading-7 text-[#475569]">
                        Deactivate User, Send Access Code, and Assign Role become available after a user is selected.
                      </p>
                    ) : null}
                  </div>

                  {pendingDeactivateUserId && selectedUser?.id === pendingDeactivateUserId ? (
                    <div
                      role="alert"
                      aria-labelledby="deactivate-user-title"
                      aria-describedby="deactivate-user-description"
                      className="rounded-3xl border border-red-200 bg-red-50 p-4"
                    >
                      <p id="deactivate-user-title" className="font-display text-2xl font-bold text-red-900">Confirm deactivation</p>
                      <p id="deactivate-user-description" className="mt-2 text-sm font-semibold leading-7 text-red-800">
                        Deactivate {selectedUser.fullName} ({selectedUser.email}) and suspend portal access for this company?
                      </p>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => void handleDeactivateUser(selectedUser.id)}
                          disabled={saving}
                          className="business-center-os-button business-center-os-button--primary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold disabled:opacity-60"
                        >
                          {saving ? "Deactivating..." : "Confirm Deactivation"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setPendingDeactivateUserId("")}
                          disabled={saving}
                          className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold disabled:opacity-60"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : null}

                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="First name">
                      <input value={form.firstName} onChange={(event) => updateForm("firstName", event.target.value)} className="business-center-os-field focus-ring min-h-12 rounded-2xl px-4 text-sm font-semibold" />
                    </Field>
                    <Field label="Last name">
                      <input value={form.lastName} onChange={(event) => updateForm("lastName", event.target.value)} className="business-center-os-field focus-ring min-h-12 rounded-2xl px-4 text-sm font-semibold" />
                    </Field>
                    <Field label="Display name">
                      <input value={form.displayName} onChange={(event) => updateForm("displayName", event.target.value)} className="business-center-os-field focus-ring min-h-12 rounded-2xl px-4 text-sm font-semibold" />
                    </Field>
                    <Field label="Full name">
                      <input value={form.fullName} onChange={(event) => updateForm("fullName", event.target.value)} className="business-center-os-field focus-ring min-h-12 rounded-2xl px-4 text-sm font-semibold" />
                    </Field>
                    <Field label="Email">
                      <input value={form.email} onChange={(event) => updateForm("email", event.target.value)} type="email" className="business-center-os-field focus-ring min-h-12 rounded-2xl px-4 text-sm font-semibold" />
                    </Field>
                    <Field label="Job title">
                      <input value={form.jobTitle} onChange={(event) => updateForm("jobTitle", event.target.value)} className="business-center-os-field focus-ring min-h-12 rounded-2xl px-4 text-sm font-semibold" />
                    </Field>
                    <Field label="Department">
                      <input value={form.department} onChange={(event) => updateForm("department", event.target.value)} className="business-center-os-field focus-ring min-h-12 rounded-2xl px-4 text-sm font-semibold" />
                    </Field>
                    <Field label="Phone">
                      <input value={form.phone} onChange={(event) => updateForm("phone", event.target.value)} className="business-center-os-field focus-ring min-h-12 rounded-2xl px-4 text-sm font-semibold" />
                    </Field>
                    <Field label="Team">
                      <input value={form.team} onChange={(event) => updateForm("team", event.target.value)} className="business-center-os-field focus-ring min-h-12 rounded-2xl px-4 text-sm font-semibold" />
                    </Field>
                    <Field label="Notifications">
                      <select value={form.notificationPreference} onChange={(event) => updateForm("notificationPreference", event.target.value as CompanyUserFormState["notificationPreference"])} className="business-center-os-field focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold">
                        <option value="both">Email and in-app</option>
                        <option value="email">Email only</option>
                        <option value="in_app">In-app only</option>
                        <option value="none">None</option>
                      </select>
                    </Field>
                    <Field label="Company">
                      <select value={form.companyId} onChange={(event) => updateForm("companyId", event.target.value)} className="business-center-os-field focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold">
                        {companies.map((company) => (
                          <option key={company.id} value={company.id}>
                            {company.companyName}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Role">
                      <select value={form.role} onChange={(event) => updateForm("role", event.target.value as BusinessCenterUserRecord["role"])} className="business-center-os-field focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold">
                        {companyUserRoleOptions.map((option) => (
                          <option key={option.key} value={option.key}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Status">
                      <select value={form.status} onChange={(event) => updateForm("status", event.target.value as BusinessCenterUserRecord["status"])} className="business-center-os-field focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold">
                        {companyStatusOptions.map((status) => (
                          <option key={status} value={status}>
                            {labelize(status)}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Portal access">
                      <select value={form.portalAccessStatus} onChange={(event) => updateForm("portalAccessStatus", event.target.value as BusinessCenterUserRecord["portalAccessStatus"])} className="business-center-os-field focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold">
                        {portalAccessOptions.map((status) => (
                          <option key={status} value={status}>
                            {labelize(status)}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Default Landing Workspace">
                      <select value={form.defaultLandingWorkspace} onChange={(event) => updateForm("defaultLandingWorkspace", event.target.value)} className="business-center-os-field focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold">
                        <option value="">Role-based fallback</option>
                        {defaultLandingWorkspaceOptions.map((option) => (
                          <option key={option.key} value={option.key}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <TextArea label="Assigned client IDs" value={form.assignedClientIdsText} onChange={(value) => updateForm("assignedClientIdsText", value)} placeholder="company-kreatethedate" />
                    <TextArea label="Assigned project IDs" value={form.assignedProjectIdsText} onChange={(value) => updateForm("assignedProjectIdsText", value)} placeholder="project-ktd-001" />
                    <TextArea label="Assigned task IDs" value={form.assignedTaskIdsText} onChange={(value) => updateForm("assignedTaskIdsText", value)} placeholder="task-ktd-001" />
                    <TextArea label="Assigned document IDs" value={form.assignedDocumentIdsText} onChange={(value) => updateForm("assignedDocumentIdsText", value)} placeholder="doc-ktd-001" />
                    <TextArea label="Notes" value={form.notes} onChange={(value) => updateForm("notes", value)} placeholder="Invite notes, scope notes, or access context." />
                  </div>

                  <div className="flex flex-wrap justify-between gap-3">
                    <div className="flex flex-wrap gap-2">
                      {selectedUser ? (
                        <>
                          <button
                            type="submit"
                            disabled={saving}
                            className="business-center-os-button business-center-os-button--primary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold disabled:opacity-60"
                          >
                            {saving ? "Saving..." : "Save User"}
                          </button>
                          <button
                            type="button"
                            onClick={handleOpenDeactivateConfirmation}
                            disabled={!selectedUserCanDeactivate || saving}
                            className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            Deactivate User
                          </button>
                          {selectedUser.email.trim()
                          && selectedUser.status === "active"
                          && selectedUser.portalAccessStatus === "active"
                          && isInternalCompanyUserRole(selectedUser.role) ? (
                            <button
                              type="button"
                              disabled={sendingLoginCode}
                              onClick={handleResendLoginCode}
                              className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold disabled:opacity-60"
                            >
                              <Send className="size-4" aria-hidden="true" />
                              {sendingLoginCode ? "Sending..." : "Send Access Code"}
                            </button>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => handleDeleteUser(selectedUser.id)}
                            className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold"
                          >
                            <Trash2 className="size-4" aria-hidden="true" />
                            Remove user
                          </button>
                        </>
                      ) : null}
                    </div>
                    {!selectedUser ? (
                      <button
                        type="submit"
                        disabled={saving}
                        className="business-center-os-button business-center-os-button--primary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold disabled:opacity-60"
                      >
                        {saving ? "Inviting..." : "Invite User"}
                      </button>
                    ) : null}
                  </div>
                </form>
                {message ? <p className="rounded-2xl bg-[rgba(20,184,166,.08)] p-4 text-sm font-semibold text-[#1F2937]">{message}</p> : null}
              </div>

              <div className="mt-5 grid gap-3">
                {visibleUsers.length ? (
                  visibleUsers.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => loadUserIntoForm(user)}
                      aria-pressed={selectedUser?.id === user.id}
                      aria-label={`Manage ${user.fullName}`}
                      className={`rounded-3xl border p-4 text-left transition ${
                        selectedUser?.id === user.id
                          ? "border-[rgba(20,184,166,.4)] bg-[rgba(20,184,166,.08)]"
                          : "border-[rgba(20,184,166,.14)] bg-white hover:border-[rgba(20,184,166,.24)]"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">{labelize(user.status)}</p>
                          <h3 className="mt-1 font-display text-2xl font-bold text-[#1F2937]">{user.fullName}</h3>
                        </div>
                        <span className="rounded-full bg-[#F5F7FA] px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-[#0F766E]">
                          {user.role.replace(/_/g, " ")}
                        </span>
                      </div>
                      <p className="mt-3 text-sm font-semibold leading-7 text-[#475569]">
                        {user.email}
                        <br />
                        Company: {companies.find((company) => company.id === user.companyId)?.companyName || user.companyId}
                        <br />
                        Portal access: {labelize(user.portalAccessStatus)}
                        <br />
                        Invitation status: {labelize(user.invitationStatus || (user.status === "active" ? "accepted" : "pending"))}
                        <br />
                        Last login: {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : "Not recorded"}
                        <br />
                        Default landing: {getDefaultLandingWorkspaceLabel(user.defaultLandingWorkspace)}
                        <br />
                        Effective permission role: {mapCompanyRoleToPermissionRole(user.role)}
                      </p>
                    </button>
                  ))
                ) : (
                  <div className="rounded-3xl border border-dashed border-[rgba(20,184,166,.2)] bg-white p-4">
                    <p className="font-display text-2xl font-bold text-[#1F2937]">No users match the selected company</p>
                    <p className="mt-2 text-sm font-semibold leading-7 text-[#475569]">
                      Company-scoped records only appear after they are assigned to the selected parent or client company.
                    </p>
                  </div>
                )}
              </div>
            </article>
          </section>
        </div>
      </section>
    </main>
  );
}

function normalizeEmail(value: string) {
  return value.trim().toLowerCase();
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="grid gap-2 text-sm font-extrabold text-[#1F2937]">
      {label}
      {children}
    </label>
  );
}

function TextArea({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="grid gap-2 text-sm font-extrabold text-[#1F2937] md:col-span-2">
      {label}
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={3}
        placeholder={placeholder}
        className="business-center-os-field focus-ring rounded-2xl px-4 py-3 text-sm font-semibold"
      />
    </label>
  );
}

function ShellLoading() {
  return (
    <main className="business-center-os-shell min-h-screen text-[#F8FAFC]">
      <div className="grid min-h-screen place-items-center">
        <div className="business-center-os-card business-center-os-card--dark rounded-3xl p-8 text-center">
          <p className="font-display text-3xl font-bold text-[#F8FAFC]">Loading company users</p>
          <p className="mt-3 text-sm font-semibold text-[#CBD5E1]">Preparing the scoped access workspace...</p>
        </div>
      </div>
    </main>
  );
}

function BusinessCenterOSHeader() {
  const navigate = useNavigate();

  async function handleLogout() {
    try {
      await logout();
    } finally {
      localStorage.removeItem("ktd_demo_auth");
      navigate("/admin/login", { replace: true });
    }
  }

  return (
    <header className="business-center-os-header sticky top-0 z-40 border-b border-[rgba(6,182,212,.16)] px-4 py-4 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-7xl flex-col gap-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <Link to="/admin/business-center-os" className="focus-ring inline-flex max-w-full items-center gap-4 rounded-3xl">
            <div className="grid size-12 place-items-center rounded-2xl bg-[linear-gradient(135deg,#FF6B35,#06B6D4)] text-sm font-black text-white shadow-[0_16px_40px_rgba(6,182,212,.18)]">
              BC
            </div>
            <span className="leading-tight">
              <span className="block text-xs font-black uppercase tracking-[0.22em] text-[#06B6D4]">Business Center OS</span>
              <span className="block font-display text-2xl font-bold text-[#F8FAFC] sm:text-[2rem]">Internal operations workspace</span>
              <span className="block text-xs font-bold uppercase tracking-[0.18em] text-[#CBD5E1]">Client records, history, and delivery planning</span>
            </span>
          </Link>

          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/admin/business-center-os"
            className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center justify-center rounded-full px-4 text-sm font-extrabold"
          >
              Back to Business Center OS
            </Link>
            <button
              type="button"
              onClick={() => void handleLogout()}
              className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-full px-4 text-sm font-extrabold"
            >
              <LogOut className="size-4" aria-hidden="true" />
              Logout
            </button>
          </div>
        </div>

        <nav className="business-center-os-nav flex gap-2 overflow-x-auto pb-1" aria-label="Business Center OS navigation">
          {businessCenterNavLinks.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `business-center-os-nav-card focus-ring inline-flex min-h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-full border px-4 text-sm font-bold transition ${
                    isActive ? "business-center-os-nav-card-active" : ""
                  }`
                }
              >
                <Icon className="size-4" aria-hidden="true" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
      </div>
    </header>
  );
}

function useBusinessCenterOSBranding(title: string, description: string) {
  useEffect(() => {
    const fullTitle = `${title} | Business Center OS`;
    document.title = fullTitle;
    document.querySelector('meta[name="description"]')?.setAttribute("content", description);

    const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const previousTheme = themeMeta?.getAttribute("content") || "";
    if (themeMeta) themeMeta.setAttribute("content", "#1F2937");

    const faviconHref = buildBusinessCenterFavicon();
    const iconSelectors = ['link[rel="icon"]', 'link[rel="shortcut icon"]'] as const;
    const icons = iconSelectors
      .map((selector) => document.querySelector<HTMLLinkElement>(selector))
      .filter((link): link is HTMLLinkElement => Boolean(link));
    const previousIcons = icons.map((link) => link.getAttribute("href"));
    icons.forEach((link) => {
      link.setAttribute("href", faviconHref);
      link.setAttribute("type", "image/svg+xml");
    });

    return () => {
      if (themeMeta && previousTheme) themeMeta.setAttribute("content", previousTheme);
      icons.forEach((link, index) => {
        const previousHref = previousIcons[index];
        if (previousHref) link.setAttribute("href", previousHref);
      });
    };
  }, [description, title]);
}

function buildBusinessCenterFavicon() {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" role="img" aria-label="Business Center OS">
      <defs>
        <linearGradient id="g" x1="8" y1="8" x2="56" y2="56" gradientUnits="userSpaceOnUse">
          <stop offset="0" stop-color="#FF6B35"/>
          <stop offset=".52" stop-color="#14B8A6"/>
          <stop offset="1" stop-color="#06B6D4"/>
        </linearGradient>
      </defs>
      <rect x="4" y="4" width="56" height="56" rx="16" fill="#1F2937"/>
      <path d="M16 16h18c9 0 16 6.5 16 14.5S43 45 34 45H16z" fill="url(#g)"/>
      <path d="M33 16h15c-4 3.3-6.8 7.6-6.8 13.6 0 6.2 3.2 10.6 7.8 15.4H33c-8.8 0-16-6.4-16-14.5S24.2 16 33 16Z" fill="#F8FAFC" opacity=".1"/>
      <text x="32" y="39" text-anchor="middle" font-size="16" font-family="Arial, Helvetica, sans-serif" font-weight="800" fill="#F8FAFC">BC</text>
    </svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function splitList(value: string) {
  return value
    .split(/[\n,]/g)
    .map((item) => item.trim())
    .filter(Boolean);
}

function labelize(value: string) {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .trim();
}

function isInternalCompanyUserRole(role: BusinessCenterUserRecord["role"]) {
  return role === "company_admin"
    || role === "company_manager"
    || role === "company_supervisor"
    || role === "company_team_member"
    || role === "viewer";
}
